import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Slice D — Persistent banner shown on the dashboard when the logged-in
 * contractor's account has ``force_password_change: true`` (pilot / invited
 * accounts). Prompts them to self-service change the password via
 * POST /api/auth/change-password. Once submitted successfully, the flag
 * clears on the backend and the banner disappears.
 */
const ChangePasswordBanner = () => {
  const { user, refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user || !user.force_password_change) return null;

  const submit = async () => {
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirmation do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. You're all set.");
      setOpen(false);
      setCurrent(""); setNext(""); setConfirm("");
      if (refreshMe) await refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="change-password-banner"
      className="jc-card p-4 mb-6 border-l-4 border-l-orange-500 bg-orange-50/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-md bg-orange-100 text-orange-700 flex items-center justify-center">
            <AlertTriangle size={18} />
          </div>
          <div>
            <div className="text-sm font-semibold text-orange-900" data-testid="change-password-banner-title">
              Change your temporary password
            </div>
            <div className="text-xs text-orange-900/80 mt-0.5">
              Your account is using a one-time setup password. Please choose a new one before continuing.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="change-password-banner-toggle"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
        >
          <KeyRound size={12} /> {open ? "Cancel" : "Change password"}
        </button>
      </div>

      {open && (
        <div className="mt-4 grid sm:grid-cols-3 gap-3 items-end">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">Current password</span>
            <input
              type="password"
              data-testid="change-password-current"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="jc-input"
              autoComplete="current-password"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">New password</span>
            <input
              type="password"
              data-testid="change-password-new"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="jc-input"
              autoComplete="new-password"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">Confirm new password</span>
            <div className="flex items-center gap-2">
              <input
                type="password"
                data-testid="change-password-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="jc-input"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                data-testid="change-password-submit"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
              >
                {saving ? <Loader2 className="animate-spin" size={12} /> : "Save"}
              </button>
            </div>
          </label>
        </div>
      )}
    </section>
  );
};

export default ChangePasswordBanner;
